import { parse } from "query-string";
import React from "react";
import { connect } from "react-redux";

import Review, { ReviewPlaceholder } from "@/mk2/apps/bazaar/components/Review";
import { ReplyFormData } from "@/mk2/apps/bazaar/components/ReviewReplyForm";
import { RECENT_REVIEW_DAYS, REVIEWS_PER_PAGE } from "@/mk2/apps/bazaar/constants";
import {
    reviewsAddReply,
    reviewsDelete,
    reviewsDeleteReply,
    reviewsLoad,
    reviewsRead,
} from "@/mk2/apps/bazaar/containers/Reviews/Reviews.actions";
import { ReviewsState } from "@/mk2/apps/bazaar/containers/Reviews/Reviews.reducers";
import {
    getReviewsSellerState,
    getReviewsState,
} from "@/mk2/apps/bazaar/containers/Reviews/Reviews.selectors";
import {
    ReviewEntity,
    ReviewRole,
    ReviewSchema,
    SellerEntity,
    SellerSchema,
} from "@/mk2/apps/bazaar/schemas";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Panel } from "@/mk2/components/Panel";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import { AppState, getRequestPermissions, getRequestUser } from "@/mk2/reducers";
import { LoadingState, isLoading } from "@/mk2/reducers/loadingState";
import { UserEntity } from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import {
    HistoryLocationWithState,
    RouteNavigateFunction,
    RoutePathPattern,
} from "@/mk2/services/browserHistory";
import {
    BAZAAR_SELLER_SHOW_OLDER,
    BAZAAR_SELLER_SHOW_OLDER_THAN_3_MONTHS,
    BZ2_REVIEWS_FROM_BUYERS,
    BZ2_REVIEWS_FROM_SELLERS,
    BZ2_SHOW_REVIEWS_FROM,
    THE_SELLER_HAS_NO_REVIEWS,
} from "@/mk/autogenerated/translations/Reviews.0f4c0022689397ae9541a5b46e444261";
import { canManageBazaar, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import styles from "./Reviews.mscss";

const PLACEHOLDER_WIDTHS = [300, 250, 320, 150, 180, 140];
const READ_TIMEOUT = 5 * 1000; // 5s

interface OwnProps {
    fromSellersPath: string;
    fromBuyersPath: string;
    routePathPattern: RoutePathPattern;
    location: HistoryLocationWithState;
    routeNavigate: RouteNavigateFunction;
    routeParams: { username: string };
}

interface StateProps {
    loadingState: LoadingState;
    seller: SellerEntity;
    reviews: ReviewEntity[];
    role: ReviewRole;
    after: number;
    hasNext: number;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    deletingIds: number[];
    deletingRepliesIds: number[];
}

interface DispatchProps {
    onLoad(username: string, role: ReviewRole, after?: number);
    onReply(reviewId: number, reply: ReplyFormData);
    onDelete(username: string, role: ReviewRole, reviewId: number);
    onDeleteReply(reviewId: number);
    onRead(username: string, role: ReviewRole, reviewIds: number[]);
}

interface State {
    showAll: boolean;
    role: ReviewRole;
}

type Props = OwnProps & StateProps & DispatchProps;

class Reviews extends React.Component<Props, State> {
    public static getDerivedStateFromProps(props: Props, state: State) {
        if (props.role !== state.role) {
            return {
                role: props.role,
                showAll: false,
            };
        }

        return null;
    }

    public state: State = {
        showAll: false,
        role: this.props.role,
    };

    private readTimer: number;

    public componentDidMount() {
        const { requestUser, seller, reviews } = this.props;

        if (seller && isAuthenticatedAs(requestUser, seller.user) && reviews.some((r) => r.isNew)) {
            this.readTimer = window.setTimeout(this.onRead, READ_TIMEOUT);
        }
    }

    public componentDidUpdate() {
        const { requestUser, seller, reviews } = this.props;

        if (seller && isAuthenticatedAs(requestUser, seller.user) && reviews.some((r) => r.isNew)) {
            window.clearTimeout(this.readTimer);
            this.readTimer = window.setTimeout(this.onRead, READ_TIMEOUT);
        }
    }

    public componentWillUnmount() {
        window.clearTimeout(this.readTimer);
    }

    public render() {
        const { loadingState, reviews, routePathPattern, fromBuyersPath, fromSellersPath, seller } =
            this.props;

        return (
            <Panel className={styles.Reviews}>
                <div className={styles.Reviews__header}>
                    <strong>{BZ2_SHOW_REVIEWS_FROM}</strong>
                    <label className={styles.Reviews__header__role}>
                        <input
                            type="radio"
                            className={styles.Reviews__header__input}
                            checked={routePathPattern === fromBuyersPath}
                            onChange={this.showReviewsFromBuyers}
                        />{" "}
                        {BZ2_REVIEWS_FROM_BUYERS}{" "}
                        <span className={styles.Reviews__header__role__count}>
                            ({seller ? seller.reviewsFromBuyersCount : 0})
                        </span>
                    </label>
                    <label className={styles.Reviews__header__role}>
                        <input
                            type="radio"
                            className={styles.Reviews__header__input}
                            checked={routePathPattern === fromSellersPath}
                            onChange={this.showReviewsFromSellers}
                        />{" "}
                        {BZ2_REVIEWS_FROM_SELLERS}{" "}
                        <span className={styles.Reviews__header__role__count}>
                            ({seller ? seller.reviewsFromSellersCount : 0})
                        </span>
                    </label>
                </div>

                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderSuccess}
                    onRenderInit={this.renderInit}
                    hasData={reviews && reviews.length > 0}
                />
            </Panel>
        );
    }

    private renderInit = () => {
        return PLACEHOLDER_WIDTHS.map((w, i) => (
            <ReviewPlaceholder key={i} textWidth={w} isLast={i + 1 === PLACEHOLDER_WIDTHS.length} />
        ));
    };

    private renderSuccess = () => {
        const {
            reviews,
            requestUserPermissions,
            seller,
            requestUser,
            role,
            hasNext,
            after,
            loadingState,
            onReply,
            deletingIds,
            deletingRepliesIds,
        } = this.props;

        if (!reviews.length) {
            // Empty state
            return <div className={styles.Reviews__empty}>{THE_SELLER_HAS_NO_REVIEWS}</div>;
        }

        const isReviewed = isAuthenticatedAs(requestUser, seller.user);
        const isAdmin = canManageBazaar(requestUserPermissions);

        const recentTime = Date.now() - RECENT_REVIEW_DAYS;
        const onlyRecent =
            !after && !this.state.showAll && reviews.some((r) => recentTime < Date.parse(r.date));

        const showedReviews = onlyRecent
            ? reviews.filter((r) => recentTime < Date.parse(r.date))
            : reviews;

        const restReviewsCount =
            (role === ReviewRole.SELLER
                ? seller.reviewsFromBuyersCount
                : seller.reviewsFromSellersCount) - showedReviews.length;

        const loading = isLoading(loadingState);

        return (
            <>
                {showedReviews.map((review, index) => (
                    <Review
                        key={review.id}
                        review={review}
                        onDelete={this.onDelete}
                        onDeleteReply={this.onDeleteReply}
                        onReply={onReply}
                        isReviewer={isAuthenticatedAs(requestUser, review.reviewer)}
                        isReviewed={isReviewed}
                        isAdmin={isAdmin}
                        isLast={reviews.length === index + 1 && !hasNext && !loading}
                        deleting={deletingIds.includes(review.id)}
                        deletingReply={deletingRepliesIds.includes(review.id)}
                    />
                ))}
                {loading &&
                    Array.from(Array(Math.min(REVIEWS_PER_PAGE, restReviewsCount)).keys()).map(
                        (index) => (
                            <ReviewPlaceholder
                                key={index}
                                isLast={index + 1 === Math.min(REVIEWS_PER_PAGE, restReviewsCount)}
                            />
                        ),
                    )}
                {(showedReviews.length < reviews.length || hasNext) && !loading && (
                    <div className={styles.Reviews__loadMore}>
                        <Btn
                            type={BtnType.BlueOutline}
                            count={restReviewsCount}
                            label={
                                showedReviews.length < reviews.length
                                    ? BAZAAR_SELLER_SHOW_OLDER_THAN_3_MONTHS
                                    : BAZAAR_SELLER_SHOW_OLDER
                            }
                            onClick={
                                showedReviews.length < reviews.length ? this.showAll : this.loadNext
                            }
                        />
                    </div>
                )}
            </>
        );
    };

    private load = () => {
        const {
            onLoad,
            role,
            after,
            routeParams: { username },
        } = this.props;
        onLoad(username, role, after);
    };

    private loadNext = () => {
        const {
            onLoad,
            role,
            reviews,
            routeParams: { username },
        } = this.props;

        onLoad(username, role, reviews[reviews.length - 1].id);
    };

    private showAll = () => {
        this.setState({ showAll: true });
    };

    private showReviewsFromBuyers = (event: React.FormEvent<HTMLInputElement>) => {
        const {
            routePathPattern,
            routeNavigate,
            fromBuyersPath,
            routeParams: { username },
        } = this.props;
        if (!event.currentTarget.checked || routePathPattern === fromBuyersPath) {
            // Ignore if unselected or if we are on same URL
            return;
        }
        routeNavigate(url(fromBuyersPath, { username }), {
            state: {
                dontScrollOnPush: true, // no reason to scroll to top
            },
        });
    };

    private showReviewsFromSellers = (event: React.FormEvent<HTMLInputElement>) => {
        const {
            routePathPattern,
            fromSellersPath,
            routeNavigate,
            routeParams: { username },
        } = this.props;
        if (!event.currentTarget.checked || routePathPattern === fromSellersPath) {
            // Ignore if unselected or if we are on same URL
            return;
        }
        routeNavigate(url(fromSellersPath, { username }), {
            state: {
                dontScrollOnPush: true, // no reason to scroll to top
            },
        });
    };

    private onDelete = (reviewId: number) => {
        const {
            onDelete,
            role,
            routeParams: { username },
        } = this.props;
        onDelete(username, role, reviewId);
    };

    private onDeleteReply = (reviewId: number) => {
        const { onDeleteReply } = this.props;
        onDeleteReply(reviewId);
    };

    private onRead = () => {
        const {
            onRead,
            reviews,
            role,
            routeParams: { username },
        } = this.props;
        // Mark read only new
        onRead(
            username,
            role,
            reviews.filter((r) => r.isNew).map((r) => r.id),
        );
    };
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    const sellerReviewsState = getReviewsState(state);
    const currentSellerState = getReviewsSellerState(state, ownProps.routeParams.username);
    const role =
        ownProps.routePathPattern === ownProps.fromBuyersPath
            ? ReviewRole.SELLER
            : ReviewRole.BUYER;
    const qs = parse(ownProps.location.search, { parseNumbers: true }) || {};

    const currentReviewsState: ReviewsState = currentSellerState[role];

    return {
        loadingState: currentReviewsState.loadingState,
        seller: getDenormalizedEntity<SellerEntity>(
            state,
            SellerSchema,
            currentSellerState.sellerId,
        ),
        reviews: getDenormalizedEntities<ReviewEntity>(
            state,
            ReviewSchema,
            currentReviewsState.ids,
        ),
        role,
        after: qs.after as number,
        hasNext: currentReviewsState.hasNext,
        requestUser: getRequestUser(state),
        requestUserPermissions: getRequestPermissions(state),
        deletingIds: sellerReviewsState.deletingIds,
        deletingRepliesIds: sellerReviewsState.deletingRepliesIds,
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onLoad: reviewsLoad,
    onReply: reviewsAddReply,
    onDelete: reviewsDelete,
    onDeleteReply: reviewsDeleteReply,
    onRead: reviewsRead,
};

export default connect(mapStateToProps, mapDispatchToProps)(Reviews);
